/**
 * kit.ts — the meshes for every piece in the armour catalogue.
 *
 * One builder per piece id, and a piece knows only how to build itself in its
 * OWN space. Where that space ends up on a moving body is `figure.ts`'s problem
 * — a piece declares a `mount` (parent me to this anchor) or a `fitting` (span
 * me along this bone) and never touches a transform of its own.
 *
 * That indirection is the whole reason sixteen pieces are tractable. The
 * previous version had eight named fields and eight bespoke posing blocks in
 * the renderer, so every new piece meant editing the render loop; a piece that
 * someone forgot to add there simply hung in the air while its owner walked
 * away from it. Now the render loop knows about anchors, not about armour.
 *
 * ## The rule everything here obeys
 *
 * Kit is drawn INSIDE the body capsule the server sweeps. Not approximately —
 * `kit.test.ts` measures every vertex of every piece against BODY_RADIUS and
 * BODY_HEIGHT in the rest pose. Armour is worn, not grown: a pauldron sticking
 * out past the hurtbox is a shoulder you can see and cannot hit, and the whole
 * render side is arranged to keep the picture and the collider agreeing.
 *
 * The three families read differently on purpose, because you have to be able
 * to tell at a glance what an opponent is wearing:
 *
 *   light  — cloth and leather. Soft silhouettes, a hood and a hanging cloak.
 *   medium — mail. A procedural ring texture over close-fitting sleeves, coif
 *            and skirt; the loose drape of a garment made of metal.
 *   heavy  — plate. Hard, bright, articulated shells with gilt edging.
 */

import * as THREE from 'three';
import {
  FEEL,
  PIECES,
  SHOULDER_HALF_WIDTH,
  type ArmorPalette,
  type Loadout,
  type Piece,
  type PieceId,
} from '@dueler/shared';
import {
  applyKitSurface,
  createSteelMaterial,
  mailRingMap,
  markNearDetail,
} from './surfaces.js';
import {
  ANKLE_HEIGHT,
  SKULL,
  LIMB_UNIT,
  PAULDRON_R,
  SHIN_R,
  THIGH_R,
  smooth,
  taperAlongY,
} from './art.js';

/**
 * Where a piece can be worn.
 *
 * MOUNTS are empties the renderer already places every frame; a piece parented
 * to one is posed for free and needs no entry in the render loop at all.
 *
 * FITTINGS are bones — two moving points — so a piece riding one has to be
 * stretched or placed along it each frame.
 */
export type MountId =
  | 'head'
  | 'chest'
  | 'pelvis'
  | 'handR'
  | 'handL'
  | 'footR'
  | 'footL'
  | 'weapon';

export type BoneId =
  | 'upperArmR'
  | 'forearmR'
  | 'upperArmL'
  | 'forearmL'
  | 'thighR'
  | 'shinR'
  | 'thighL'
  | 'shinL';

/** A piece parented to a posed empty. No per-frame work. */
export interface Mount {
  at: MountId;
  object: THREE.Object3D;
}

/**
 * A piece riding a bone.
 *
 * `span` stretches it to the bone's exact length, which is right for anything
 * that behaves like a sleeve. `ride` places it at `t` along the bone and turns
 * it to match, without stretching, which is right for a rigid plate — a cuisse
 * does not get longer when a leg does.
 */
export interface Fitting {
  on: BoneId;
  object: THREE.Object3D;
  fit: 'span' | 'ride';
  /** For `ride`: 0 at the proximal joint, 1 at the distal one. */
  t?: number;
}

/**
 * The parts of the bare figure a piece can replace.
 *
 * Coarser than the bone list on purpose: this is about what a garment covers,
 * and no piece of kit covers half a shin.
 */
export const BODY_PARTS = [
  'head',
  'torso',
  'shoulders',
  'upperArms',
  'elbows',
  'forearms',
  'hands',
  'thighs',
  'knees',
  'shins',
  'feet',
] as const;
export type BodyPart = (typeof BODY_PARTS)[number];

export interface BuiltKit {
  mounts: Mount[];
  fittings: Fitting[];
  disposables: { dispose(): void }[];
  /** True if any piece brings its own blade, so the plain pill must be hidden. */
  hidesBareBlade: boolean;
  /**
   * Body parts this kit replaces, which the figure hides while it is worn.
   *
   * This is the systematic answer to armour clipping, and it is what real games
   * do: you do not fight a shell and the limb inside it into agreement, you take
   * the limb away. Two curved surfaces a few millimetres apart will always poke
   * through each other somewhere across a full range of motion — no amount of
   * tuning radii fixes that, it only moves where it happens.
   *
   * A piece only claims a part it FULLY encloses. A hood leaves the face open,
   * so it keeps the head; a great helm does not, so it takes it.
   */
  hides: Set<BodyPart>;
}

// ---------------------------------------------------------------------------
// materials
// ---------------------------------------------------------------------------

/**
 * A tiling chainmail texture, drawn once and shared by every mail piece.
 *
 * Rows of overlapping rings, offset every other row, on a dark ground. This is
 * the single thing that makes medium armour read as mail rather than as grey
 * plate: the silhouette of a hauberk and a cuirass are not that different, and
 * the surface is what tells them apart at duelling distance.
 *
 * Built lazily and cached module-wide — one canvas for the whole session, not
 * one per player. Returns undefined where there is no DOM (the unit tests run
 * in Node), and every caller falls back to a plain material, so kit still
 * builds and can still be measured.
 */
let mailTexture: THREE.Texture | undefined | null = null;

function getMailTexture(): THREE.Texture | undefined {
  if (mailTexture !== null) return mailTexture ?? undefined;
  mailTexture = undefined;
  if (typeof document === 'undefined') return undefined;

  const SIZE = 128;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return undefined;

  ctx.fillStyle = '#1b1e24';
  ctx.fillRect(0, 0, SIZE, SIZE);

  const ROWS = 8;
  const step = SIZE / ROWS;
  const radius = step * 0.42;
  ctx.lineWidth = Math.max(1, step * 0.16);
  for (let row = -1; row <= ROWS; row += 1) {
    for (let col = -1; col <= ROWS; col += 1) {
      // Every other row is offset half a ring, which is what makes the rings
      // interlock instead of stacking into a grid.
      const cx = col * step + (row % 2 === 0 ? 0 : step / 2);
      const cy = row * step * 0.72;
      // A highlight arc on top and a shadow arc below: the whole illusion of
      // roundness in two strokes, which is all a texture this size can carry.
      ctx.strokeStyle = '#9aa6b5';
      ctx.beginPath();
      ctx.arc(cx, cy, radius, Math.PI, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = '#4d545e';
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI);
      ctx.stroke();
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 4);
  mailTexture = texture;
  return texture;
}

/** The materials a piece paints itself with, made once per piece. */
interface Paints {
  /** The piece's main surface: plate steel, mail, or cloth. */
  main: THREE.MeshStandardMaterial;
  /** Edging, buckles, rivets, gilt. */
  trim: THREE.MeshStandardMaterial;
  /** Padding, straps and lining showing at the joints. */
  cloth: THREE.MeshStandardMaterial;
  /** Team colour, so you can still tell who is who once both are in steel. */
  team: THREE.MeshStandardMaterial;
}

function makePaints(piece: Piece, isSelf: boolean, disposables: { dispose(): void }[]): Paints {
  const palette: ArmorPalette = piece.palette;
  const heavy = piece.weightClass === 'heavy';
  const mail = piece.weightClass === 'medium';

  // Heavy plate must stay ROUGH. Mirror metalness turns every cylinder into a
  // chrome balloon and kills the silhouette of real armour.
  const main = new THREE.MeshStandardMaterial({
    color: palette.steel,
    metalness: heavy ? 0.78 : mail ? 0.72 : 0.04,
    roughness: heavy ? 0.58 : mail ? 0.55 : 0.86,
    envMapIntensity: heavy ? 0.55 : mail ? 0.65 : 0.4,
  });
  applyKitSurface(main, heavy ? 'heavy' : mail ? 'medium' : 'light');
  if (heavy && main.bumpScale !== undefined) main.bumpScale = 0.55;
  const trim = new THREE.MeshStandardMaterial({
    color: palette.trim,
    metalness: heavy ? 0.7 : 0.25,
    roughness: heavy ? 0.48 : 0.7,
    envMapIntensity: heavy ? 0.6 : 0.5,
  });
  if (heavy) applyKitSurface(trim, 'heavy');
  const cloth = new THREE.MeshStandardMaterial({ color: palette.cloth, roughness: 0.92 });
  const team = new THREE.MeshStandardMaterial({
    color: isSelf ? 0x3f6fb5 : 0xa8483a,
    roughness: 0.85,
  });
  disposables.push(main, trim, cloth, team);
  return { main, trim, cloth, team };
}

/**
 * Everything a piece builder is handed.
 *
 * `add` both registers the geometry for disposal and parents the mesh, so a
 * builder cannot leak a geometry by forgetting — the only way to make a mesh is
 * the way that also cleans it up.
 */
interface Brush {
  paints: Paints;
  add(geo: THREE.BufferGeometry, mat: THREE.Material, parent: THREE.Object3D): THREE.Mesh;
  keep(...items: { dispose(): void }[]): void;
}

/** A capsule authored at LIMB_UNIT so `span` can stretch it to a real bone. */
function sleeve(brush: Brush, radius: number, mat: THREE.Material): THREE.Mesh {
  const geo = new THREE.CapsuleGeometry(radius, LIMB_UNIT, 6, 12);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.userData['radius'] = radius;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  brush.keep(geo);
  return mesh;
}

/** Row of rivets along a parent — near-only LOD detail. */
function rivetRow(
  brush: Brush,
  parent: THREE.Object3D,
  mat: THREE.Material,
  count: number,
  radius: number,
  y: number,
  spread: number,
): void {
  // One geometry per rivet: brush.add registers the geo for dispose, and
  // sharing one geo across adds would list it many times and break lifetime
  // accounting (and double-dispose).
  for (let i = 0; i < count; i += 1) {
    const t = count === 1 ? 0.5 : i / (count - 1);
    const ang = (t - 0.5) * spread;
    const rivet = brush.add(new THREE.SphereGeometry(0.0045, 8, 6), mat, parent);
    rivet.position.set(Math.sin(ang) * radius, y, -Math.cos(ang) * radius);
    markNearDetail(rivet);
  }
}

/**
 * A skirt: an open-ended, optionally flared cone.
 *
 * Double-sided, because you can see the inside of a hanging skirt from below
 * and a single-sided one simply vanishes when you do.
 */
function skirtGeometry(top: number, bottom: number, height: number, segments = 20): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(top, bottom, height, segments, 2, true);
}

/**
 * Lathe a vertical plate profile (y, radius) into a shell of beaten steel.
 * Far less "balloon" than a sphere/capsule — this is how a breastplate is made.
 */
function latheProfile(
  points: ReadonlyArray<readonly [number, number]>,
  segments = 20,
): THREE.LatheGeometry {
  const vecs = points.map(([y, r]) => new THREE.Vector2(r, y));
  const geo = new THREE.LatheGeometry(vecs, segments);
  geo.computeVertexNormals();
  return geo;
}

/**
 * Pulls one face of a round shell inward so it reads as beaten plate rather
 * than a balloon. `keep` is how much of the original half-depth remains on
 * that face (0.55 ≈ a breastplate's shallow curve).
 */
function flattenFace(
  geometry: THREE.BufferGeometry,
  axis: 'x' | 'z',
  toward: -1 | 1,
  keep = 0.55,
): void {
  const pos = geometry.attributes['position'] as THREE.BufferAttribute;
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const min = axis === 'x' ? box.min.x : box.min.z;
  const max = axis === 'x' ? box.max.x : box.max.z;
  const mid = (min + max) / 2;
  const half = (max - min) / 2 || 1;
  const faceLimit = mid + toward * half * keep;
  for (let i = 0; i < pos.count; i += 1) {
    const v = axis === 'x' ? pos.getX(i) : pos.getZ(i);
    if (toward < 0 ? v >= mid : v <= mid) continue;
    // Stronger flatten near the face centre (high |v|), softer toward the rim.
    const t = Math.abs(v - mid) / half;
    const next = v * (1 - t * 0.85) + faceLimit * (t * 0.85);
    if (axis === 'x') pos.setX(i, next);
    else pos.setZ(i, next);
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
}

/**
 * Open half-cylinder lame (plate wrapping the front of a limb).
 * `thetaLength` defaults to a bit over half so the edges tuck in.
 */
function plateLame(
  radiusTop: number,
  radiusBottom: number,
  height: number,
  segments = 20,
  thetaLength = Math.PI * 1.15,
): THREE.CylinderGeometry {
  const geo = new THREE.CylinderGeometry(
    radiusTop,
    radiusBottom,
    height,
    segments,
    2,
    true,
    -thetaLength / 2,
    thetaLength,
  );
  geo.computeVertexNormals();
  return geo;
}

/** Thin rectangular plate, slightly curved by scaling Z — for tassets / haute. */
function curvedPlate(w: number, h: number, thick: number, segs = 6): THREE.BoxGeometry {
  const geo = new THREE.BoxGeometry(w, h, thick, segs, segs, 1);
  const pos = geo.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i += 1) {
    const y = pos.getY(i);
    const x = pos.getX(i);
    // Gentle barrel curve so a flat box reads as hammered plate.
    pos.setZ(i, pos.getZ(i) - (x * x) * 2.2 - (y * y) * 0.8);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/**
 * Slim closed plate along a bone — full cylinders (no open backs / missing metal),
 * overlapping and tapering distal. Mid-limb must stay thinner than pauldron/couter.
 */
function articulatedLimb(
  brush: Brush,
  kit: BuiltKit,
  bone: BoneId,
  radius: number,
  lameCount = 5,
): void {
  const { paints } = brush;
  // Thin closed under-sleeve so articulation gaps never flash flesh.
  const under = sleeve(brush, radius * 0.82, paints.main);
  kit.fittings.push({ on: bone, object: under, fit: 'span' });

  for (let i = 0; i < lameCount; i += 1) {
    const t = 0.08 + (i / Math.max(1, lameCount - 1)) * 0.84;
    const distal = i / Math.max(1, lameCount - 1);
    // Strong anatomical taper: wider near the proximal joint, slim at the far end.
    const r = radius * (1.06 - distal * 0.28);
    const g = new THREE.Group();
    const lame = brush.add(
      new THREE.CylinderGeometry(r, r * 0.96, 0.048, 16, 1, false),
      paints.main,
      g,
    );
    // Subtle overlap so stacked lames read continuous, not Michelin.
    lame.scale.set(1, 1.15, 1);
    if (i === 0 || i === lameCount - 1) {
      const rim = brush.add(new THREE.TorusGeometry(r * 0.98, 0.003, 5, 14), paints.trim, g);
      rim.rotation.x = Math.PI / 2;
      rim.position.y = i === 0 ? 0.016 : -0.016;
    }
    kit.fittings.push({ on: bone, object: g, fit: 'ride', t });
  }
}

// ---------------------------------------------------------------------------
// the pieces
// ---------------------------------------------------------------------------

/**
 * Body measurements every piece is fitted against, so kit and flesh agree.
 *
 * Read from `art.ts` and `feel.ts` rather than guessed at: the moment a piece
 * hard-codes the thigh radius it was drawn against, changing the leg silently
 * puts plate inside it.
 */
const BODY_R = FEEL.BODY_RADIUS;
/** How far the shoulder points sit off the spine, m. */
const SHOULDER_X = SHOULDER_HALF_WIDTH;
/** Chest-space Y of the waist, relative to the shoulder line. Negative is down. */
const WAIST_Y = -0.5;

/**
 * Highest a chest piece may reach, in chest space.
 *
 * Just below the shoulder line. A breastplate closes round the shoulders and
 * stops; above that is neck, and neck belongs to the helm. The cuirass used to
 * reach 15cm past this and swallowed the head whole — the skull was inside the
 * shell with only its crown showing.
 */
const CHEST_TOP = -0.02;

/**
 * Where a shoulder piece sits, and how big it may be.
 *
 * The shoulders are the widest part of the body, and the chest anchor CARRIES
 * FORWARD by up to TORSO_LUNGE_MAX on a thrust — so the worst case is not the
 * rest pose, it is a lunge, where the shoulder point is `hypot(x, lunge)` from
 * the spine. Both numbers are derived from that budget rather than picked, or
 * a pauldron that looks fine standing still escapes the hurtbox mid-thrust.
 */

/**
 * Widest anything hanging from the chest or head anchor may be, m.
 *
 * Same budget seen from the middle: a piece centred on the spine gets the whole
 * radius less the lunge. Long hanging pieces get less again — see `DRAPE_R` —
 * because the anchor LEANS as well as carrying forward, and a lean swings the
 * bottom of a long garment much further than it moves the top.
 */
const TRUNK_R = BODY_R - FEEL.TORSO_LUNGE_MAX - 0.014;
/** As `TRUNK_R`, for the hem of something that hangs a body's-length down. */
const DRAPE_R = TRUNK_R - 0.05;

type Builder = (brush: Brush, kit: BuiltKit) => void;

// ---- helm ----

/** Light: a wool cowl with a mantle over the shoulders. */
const buildHood: Builder = (brush, kit) => {
  const { paints } = brush;
  const helm = new THREE.Group();
  const crown = 0.12;

  // The cowl: a cone over the skull, drawn back into a soft point.
  const cowlGeo = new THREE.SphereGeometry(0.145, 20, 16, 0, Math.PI * 2, 0, Math.PI * 0.62);
  const cowl = brush.add(cowlGeo, paints.main, helm);
  cowl.scale.set(1, 1.24, 1.1);
  cowl.position.y = crown;

  // The cowl drawn back into a soft point. Tipped BACKWARDS rather than up, so
  // the peak spends its length behind the head instead of above the crown of
  // the hurtbox, where nothing can be hit.
  const peak = brush.add(new THREE.ConeGeometry(0.08, 0.16, 14), paints.main, helm);
  peak.position.set(0, crown + 0.055, 0.085);
  peak.rotation.x = -1.15;

  // The mantle, hanging off the shoulders. Flared as far as the shoulder budget
  // allows and no further.
  const mantleGeo = skirtGeometry(0.13, TRUNK_R, 0.2, 20);
  const mantleMat = paints.main.clone();
  mantleMat.side = THREE.DoubleSide;
  brush.keep(mantleMat);
  const mantle = brush.add(mantleGeo, mantleMat, helm);
  mantle.position.y = -0.03;

  const cordGeo = new THREE.TorusGeometry(0.112, 0.011, 8, 20);
  const cord = brush.add(cordGeo, paints.trim, helm);
  cord.rotation.x = Math.PI / 2;
  cord.position.y = 0.045;

  kit.mounts.push({ at: 'head', object: helm });
};

/**
 * Medium: an open-faced soldier's helm — a domed skull with long cheek plates
 * and a flared neck guard. No visor: the face is open.
 *
 * Every dimension is derived from `SKULL` rather than chosen, and that is the
 * fix for what was wrong with the piece it replaces. The mail coif was built to
 * hand-picked numbers, and when the head was later raised to clear the
 * shoulders it was left behind — so the crown poked straight out through the
 * top of it. A helm sized against the head it is worn on cannot be outgrown.
 *
 * The dome is a theta-limited CAP rather than a full sphere, so it covers the
 * crown and stops at the brow. A full sphere would enclose the face too, which
 * is the one thing an open helm must not do.
 */
const buildSoldiersHelm: Builder = (brush, kit) => {
  const { paints } = brush;
  const helm = new THREE.Group();

  /** Steel stands off the skull by a padded lining's thickness. */
  const LINING = 0.016;
  const R = SKULL.rx + LINING;
  const RY = SKULL.ry + LINING;
  /** How far past the skull's equator the dome reaches, as a fraction of pi. */
  const BROW = 0.6;

  const domeMat = paints.main.clone();
  // Open at the bottom, so it needs both faces or the inside of the helm
  // disappears when you look up into it.
  domeMat.side = THREE.DoubleSide;
  brush.keep(domeMat);

  const dome = brush.add(
    new THREE.SphereGeometry(R, 26, 18, 0, Math.PI * 2, 0, Math.PI * BROW),
    domeMat,
    helm,
  );
  dome.scale.set(1, RY / R, 1.04);
  dome.position.y = SKULL.centreY;

  /** Where the dome's lower edge sits — the brow line. */
  const browY = SKULL.centreY - RY * Math.sin((BROW - 0.5) * Math.PI);
  /** Half-width of the dome at that height. */
  const browR = R * Math.cos((BROW - 0.5) * Math.PI);

  // A low comb front to back, following the dome rather than cutting through
  // it: a torus of the dome's own radius can only sit on the surface.
  const comb = brush.add(new THREE.TorusGeometry(R * 1.01, 0.01, 8, 26, Math.PI), paints.trim, helm);
  comb.rotation.set(0, Math.PI / 2, -Math.PI / 2);
  comb.scale.set(RY / R, 1, 1);
  comb.position.y = SKULL.centreY;

  // The brow band, a rolled rim closing the dome's edge.
  const band = brush.add(new THREE.TorusGeometry(browR, 0.012, 8, 28), paints.trim, helm);
  band.rotation.x = Math.PI / 2;
  band.scale.set(1, 1, 1.04);
  band.position.y = browY;

  // A short peak over the eyes. A brim, NOT a visor — it shades the face and
  // leaves it open, which is the whole character of the helm.
  const peak = brush.add(new THREE.CylinderGeometry(0.07, 0.05, 0.012, 18, 1, false, 0, Math.PI), paints.main, helm);
  peak.rotation.set(-0.45, Math.PI / 2, 0);
  peak.scale.set(1, 1, 1.5);
  peak.position.set(0, browY + 0.006, -browR * 0.84);

  // Cheek plates: long, hinged at the band, angled out at the bottom. These
  // and the neck guard are what make it read as a soldier's helm at distance.
  for (const side of [-1, 1] as const) {
    const cheek = brush.add(
      new THREE.SphereGeometry(0.062, 16, 14, 0, Math.PI * 2, 0, Math.PI * 0.55),
      paints.main,
      helm,
    );
    cheek.scale.set(0.42, 1.5, 1.1);
    cheek.rotation.set(0.12, 0, side * 0.22);
    cheek.position.set(side * browR * 0.84, browY - 0.026, -0.012);
    const hinge = brush.add(new THREE.SphereGeometry(0.013, 10, 8), paints.trim, helm);
    hinge.position.set(side * browR * 0.92, browY - 0.004, -0.014);
  }

  // The neck guard, flaring back and down off the nape.
  const nape = brush.add(
    new THREE.SphereGeometry(0.086, 18, 14, 0, Math.PI * 2, 0, Math.PI * 0.5),
    paints.main,
    helm,
  );
  nape.scale.set(1.05, 0.9, 0.6);
  nape.rotation.x = -1.05;
  nape.position.set(0, browY - 0.026, browR * 0.7);
  const napeRim = brush.add(new THREE.TorusGeometry(0.084, 0.008, 6, 22, Math.PI), paints.trim, helm);
  napeRim.rotation.set(Math.PI / 2 - 0.5, 0, 0);
  napeRim.position.set(0, browY - 0.062, browR * 0.94);

  // Mail hanging from the band onto the shoulders. The helm is steel; the
  // aventail under it is rings, which is what keeps it in the mail family.
  const capeMat = paints.main.clone();
  capeMat.side = THREE.DoubleSide;
  brush.keep(capeMat);
  const cape = brush.add(skirtGeometry(browR * 0.98, browR * 1.45, 0.15, 22), capeMat, helm);
  cape.position.y = browY - 0.08;

  kit.mounts.push({ at: 'head', object: helm });
};

/**
 * Closed sugarloaf bascinet bowl — watertight sphere topology morphed into a
 * tall pointed shell. Open lathes ghost; plain spheres read as ovals.
 */
function sugarloafBowl(radius: number): THREE.BufferGeometry {
  const geo = new THREE.SphereGeometry(radius, 24, 18);
  const pos = geo.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i += 1) {
    let x = pos.getX(i);
    let y = pos.getY(i);
    let z = pos.getZ(i);
    const ny = y / radius; // -1..1
    // Pinch toward a point at the apex; keep the cheeks fuller.
    const apex = Math.max(0, ny);
    const pinch = 1 - apex * apex * 0.68;
    x *= pinch;
    // Back fuller; face only gently flattened so the snout seats without
    // opening a hole into the empty skull (that read as a black void).
    const face = z < 0 ? 0.88 + 0.12 * (1 - apex) : 1.1;
    z *= pinch * face;
    y *= 1.48;
    if (ny > 0.55) y += (ny - 0.55) * radius * 0.55;
    pos.setXYZ(i, x, y, z);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/**
 * Heavy: hounskull bascinet (c. 1380–1420). Id stays `armet` for the codec.
 *
 * Closed steel everywhere the camera can see into — a scooped face + black
 * cloth slits was reading as a pitch-black void instead of a visor.
 */
const buildArmet: Builder = (brush, kit) => {
  const { paints } = brush;
  const helm = new THREE.Group();
  const steel = paints.main;
  const brass = paints.trim;
  steel.transparent = false;
  steel.depthWrite = true;
  steel.side = THREE.FrontSide;
  const cy = SKULL.centreY;

  // Dark recessed STEEL for ocularia / breaths — not void-black cloth.
  const recess = steel.clone();
  recess.color = new THREE.Color(0x3a424c);
  recess.metalness = 0.55;
  recess.roughness = 0.7;
  recess.envMapIntensity = 0.25;
  brush.keep(recess);

  // ---- Closed sugarloaf bowl ----
  const bowl = brush.add(sugarloafBowl(0.12), steel, helm);
  bowl.position.set(0, cy + 0.01, 0.015);

  // Face seal — solid steel behind the snout so nothing looks into empty head space.
  const faceSeal = brush.add(new THREE.SphereGeometry(0.1, 18, 14), steel, helm);
  faceSeal.scale.set(0.92, 0.88, 0.5);
  faceSeal.position.set(0, cy - 0.01, -0.055);

  // Brass brow band hugging the bowl.
  const browBand = brush.add(new THREE.TorusGeometry(0.108, 0.006, 6, 28), brass, helm);
  browBand.rotation.x = Math.PI / 2;
  browBand.scale.set(1.0, 1, 0.88);
  browBand.position.set(0, cy + 0.05, -0.01);

  // ---- Snout cone. Double-sided so the underside never culls to void. ----
  const snoutLen = 0.3;
  const snoutR = 0.1;
  const snoutSx = 0.7;
  const snoutSz = 1.15;
  const snoutMat = steel.clone();
  snoutMat.side = THREE.DoubleSide;
  brush.keep(snoutMat);
  const snout = brush.add(new THREE.ConeGeometry(snoutR, snoutLen, 12, 1, false), snoutMat, helm);
  snout.rotation.x = -Math.PI / 2 + 0.2;
  snout.scale.set(snoutSx, 1, snoutSz);
  snout.position.set(0, cy - 0.005, -0.04 - snoutLen * 0.3);

  // Ocularia — thin horizontal recesses in dark steel (not a black face panel).
  for (const side of [-1, 1] as const) {
    const d = snoutLen * 0.78;
    const y = snoutLen / 2 - d;
    const r = (snoutR * d) / snoutLen;
    const slit = brush.add(new THREE.BoxGeometry(0.042, 0.007, 0.016), recess, snout);
    slit.position.set(side * 0.03, y, r * 0.9);
    slit.scale.set(1 / snoutSx, 1, 1 / snoutSz);
  }

  // Breath holes — small dark-steel dimples on the right cheek.
  for (let row = 0; row < 3; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      const d = 0.08 + col * 0.04;
      const y = snoutLen / 2 - d;
      const r = ((snoutR * d) / snoutLen) * 0.9;
      const theta = 0.65 + row * 0.3;
      const hole = brush.add(new THREE.SphereGeometry(0.004, 6, 5), recess, snout);
      hole.position.set(Math.sin(theta) * r, y, Math.cos(theta) * r);
      hole.scale.set(1 / snoutSx, 1, 1 / snoutSz);
      markNearDetail(hole);
    }
  }

  // Temple hinges.
  for (const side of [-1, 1] as const) {
    const pin = brush.add(new THREE.CylinderGeometry(0.01, 0.01, 0.028, 10), brass, helm);
    pin.rotation.z = Math.PI / 2;
    pin.position.set(side * 0.12, cy + 0.02, -0.015);
  }

  // Vervelle line at the mail attach.
  const brim = brush.add(new THREE.TorusGeometry(0.098, 0.006, 6, 28), brass, helm);
  brim.rotation.x = Math.PI / 2;
  brim.scale.set(1.05, 1, 0.95);
  brim.position.set(0, cy - 0.12, 0.02);

  // Aventail — open mail skirt (double-sided on purpose).
  const foot = FEEL.BODY_HEIGHT * (1 - FEEL.HEAD_ZONE_FRACTION) - FEEL.SLASH_PIVOT[1];
  const aventailMat = steel.clone();
  aventailMat.side = THREE.DoubleSide;
  const rings = mailRingMap();
  if (rings !== undefined) {
    aventailMat.map = rings;
    aventailMat.bumpMap = rings;
    aventailMat.bumpScale = 0.9;
  }
  brush.keep(aventailMat);
  const aventailH = Math.abs(foot) + 0.12;
  const aventail = brush.add(skirtGeometry(0.11, 0.175, aventailH, 28), aventailMat, helm);
  aventail.scale.set(1.05, 1, 0.98);
  aventail.position.y = cy - 0.14 - aventailH * 0.22;

  kit.mounts.push({ at: 'head', object: helm });
};

// ---- shoulders ----

/**
 * Builds a matched pair on the shoulder points.
 *
 * Mounted on the CHEST anchor at ±SHOULDER_HALF_WIDTH, which is the same
 * transform the shoulders themselves are derived from — so a shoulder piece
 * lands exactly on the joint the arm is solved from and cannot come adrift from
 * the arm hanging out of it.
 */
function shoulderPair(kit: BuiltKit, cap: (sign: number) => THREE.Object3D): void {
  const trunk = new THREE.Group();
  for (const sign of [-1, 1] as const) {
    const piece = cap(sign);
    piece.position.set(sign * SHOULDER_HALF_WIDTH, -0.005, 0);
    trunk.add(piece);
  }
  kit.mounts.push({ at: 'chest', object: trunk });
}

/** Light: boiled leather caps, buckled at the arm. */
const buildSpaulders: Builder = (brush, kit) => {
  const { paints } = brush;
  shoulderPair(kit, () => {
    const cap = new THREE.Group();
    const shell = brush.add(new THREE.SphereGeometry(PAULDRON_R * 0.72, 18, 14), paints.main, cap);
    shell.scale.set(1, 0.74, 1);
    // A rolled leather edge, and the buckle that holds it on.
    const edge = brush.add(new THREE.TorusGeometry(PAULDRON_R * 0.66, 0.013, 8, 20), paints.trim, cap);
    edge.rotation.x = Math.PI / 2;
    edge.position.y = -0.028;
    const buckle = brush.add(new THREE.BoxGeometry(0.03, 0.024, 0.018), paints.trim, cap);
    buckle.position.set(0, -0.03, -PAULDRON_R * 0.52);
    return cap;
  });
};

/** Medium: mail voiders over the joint the sleeve leaves open. */
const buildMailVoiders: Builder = (brush, kit) => {
  const { paints } = brush;
  shoulderPair(kit, () => {
    const cap = new THREE.Group();
    const shell = brush.add(new THREE.SphereGeometry(PAULDRON_R * 0.8, 20, 16), paints.main, cap);
    shell.scale.set(1, 0.86, 1);
    // Mail hanging a little further down the arm than the leather does — it
    // drapes rather than holding a shape, which is the whole read of mail.
    const skirtMat = paints.main.clone();
    skirtMat.side = THREE.DoubleSide;
    brush.keep(skirtMat);
    const drape = brush.add(
      skirtGeometry(PAULDRON_R * 0.76, PAULDRON_R * 0.66, 0.075, 20),
      skirtMat,
      cap,
    );
    drape.position.y = -0.05;
    const strap = brush.add(new THREE.TorusGeometry(PAULDRON_R * 0.72, 0.011, 8, 20), paints.trim, cap);
    strap.rotation.x = Math.PI / 2;
    strap.position.y = 0.012;
    return cap;
  });
};

/**
 * Heavy: gothic pauldron — the BIGGEST joint on the arm.
 *
 * Smooth closed dome over the deltoid (refs), not bent flat boxes that read as
 * hollow bands with gaps. Hierarchy: pauldron > couter > slim tapered limbs.
 */
const buildPauldrons: Builder = (brush, kit) => {
  const { paints } = brush;
  shoulderPair(kit, (sign) => {
    const cap = new THREE.Group();
    // Outboard so mass cups the arm, not the neck/head capsule.
    const OUT = sign * 0.032;

    // ---- Smooth shoulder cup (the missing volume) ----
    // Hemisphere shell, scaled into a deep deltoid dome. Closed metal — no
    // open backs, no faceted plaques.
    const dome = brush.add(
      new THREE.SphereGeometry(0.09, 22, 16, 0, Math.PI * 2, 0, Math.PI * 0.68),
      paints.main,
      cap,
    );
    dome.scale.set(1.2, 0.92, 1.28);
    dome.position.set(OUT, 0.022, 0.0);
    dome.rotation.z = sign * 0.18;
    dome.rotation.x = -0.2;

    // Secondary overlapping shell for layered plate read.
    const shell = brush.add(
      new THREE.SphereGeometry(0.082, 20, 14, 0, Math.PI * 2, 0, Math.PI * 0.58),
      paints.main,
      cap,
    );
    shell.scale.set(1.15, 0.88, 1.22);
    shell.position.set(OUT + sign * 0.006, 0.008, -0.012);
    shell.rotation.z = sign * 0.14;
    shell.rotation.x = -0.28;

    // Brass rim under the dome.
    const rim = brush.add(new THREE.TorusGeometry(0.072, 0.005, 6, 20), paints.trim, cap);
    rim.rotation.x = Math.PI / 2 + 0.15;
    rim.scale.set(1.15, 1, 1.2);
    rim.position.set(OUT, -0.02, -0.01);

    // Haute-piece — modest neck flange, kept out of the head capsule.
    const haute = brush.add(curvedPlate(0.03, 0.05, 0.012, 4), paints.main, cap);
    haute.position.set(-sign * 0.03, 0.055, 0.005);
    haute.rotation.z = sign * 0.42;

    // Closed wrapping lames hanging into the rerebrace — taper hard.
    for (let i = 0; i < 4; i += 1) {
      const t = i / 3;
      const r = 0.07 - t * 0.022;
      const lame = brush.add(
        new THREE.CylinderGeometry(r, r - 0.008, 0.03, 16, 1, false),
        paints.main,
        cap,
      );
      lame.position.set(OUT + sign * 0.012, -0.028 - i * 0.024, -0.005);
      lame.rotation.z = sign * 0.08;
      lame.scale.set(1.05, 1, 1.1);
    }

    // Besagew covers the armpit gap the open lames used to leave.
    const besagew = brush.add(new THREE.SphereGeometry(0.028, 12, 10), paints.main, cap);
    besagew.scale.set(1, 0.7, 1);
    besagew.position.set(OUT + sign * 0.01, -0.02, -0.055);
    return cap;
  });
};

// ---- chest ----

/** Light: a quilted gambeson under a long wool cloak. */
const buildCloak: Builder = (brush, kit) => {
  const { paints } = brush;
  const trunk = new THREE.Group();

  // The gambeson, as horizontal quilted rolls down the trunk. Cheap, and the
  // ribbing is exactly what says "padded" rather than "shirt".
  const ROLLS = 7;
  for (let i = 0; i < ROLLS; i += 1) {
    const t = i / (ROLLS - 1);
    // Starts below the shoulder line, not at it: the chest anchor leans in the
    // guard stance and tips the top roll's front rim up under the chin.
    const y = smooth(CHEST_TOP - 0.07, WAIST_Y - 0.04, t);
    const radius = smooth(0.185, 0.155, t);
    const roll = brush.add(new THREE.CylinderGeometry(radius, radius, 0.075, 20), paints.cloth, trunk);
    roll.scale.set(1, 1, 0.84);
    roll.position.y = y;
  }

  // The cloak: a broad panel hanging off the shoulders and down the back,
  // flaring as it falls. Kept inside the capsule — a cloak that clipped through
  // the hurtbox would be the one bit of kit lying about where the body is.
  const cloakGeo = skirtGeometry(0.19, DRAPE_R, 0.82, 24);
  const cloakMat = paints.main.clone();
  cloakMat.side = THREE.DoubleSide;
  brush.keep(cloakMat);
  const cloak = brush.add(cloakGeo, cloakMat, trunk);
  // Squashed front-to-back and pushed backwards, so it hangs BEHIND the man
  // rather than wrapping him like a barrel.
  cloak.scale.set(1, 1, 0.62);
  // Hung from well below the shoulder line. Its top rim is the highest thing on
  // the piece, and the chest anchor LEANS in the guard stance — level with the
  // shoulders, the front of the rim tipped up under the wearer's chin.
  cloak.position.set(0, -0.5, 0.05);

  // The clasp across the collarbones, and a mantle over the shoulders.
  const mantleGeo = skirtGeometry(0.15, TRUNK_R, 0.15, 22);
  const mantleMat = paints.main.clone();
  mantleMat.side = THREE.DoubleSide;
  brush.keep(mantleMat);
  const mantle = brush.add(mantleGeo, mantleMat, trunk);
  // Hung lower than the shoulder line rather than level with it. The chest
  // anchor LEANS in the guard stance, which tips the mantle's front rim up and
  // forward — level, it ended up under the wearer's chin.
  mantle.position.y = CHEST_TOP - 0.1;

  const clasp = brush.add(new THREE.TorusGeometry(0.03, 0.012, 8, 16), paints.trim, trunk);
  clasp.rotation.x = Math.PI / 2;
  clasp.position.set(0, -0.02, -0.13);

  // A surcoat in the wearer's colours over the gambeson. Every chest piece
  // carries one: kit replaces the bare tabard, so without it putting on armour
  // would make you unidentifiable.
  const surcoat = brush.add(new THREE.BoxGeometry(0.185, 0.4, 0.02, 1, 4, 1), paints.team, trunk);
  surcoat.position.set(0, -0.27, -0.152);

  const belt = brush.add(new THREE.TorusGeometry(0.152, 0.019, 8, 22), paints.trim, trunk);
  belt.rotation.x = Math.PI / 2;
  belt.position.y = WAIST_Y + 0.02;

  kit.mounts.push({ at: 'chest', object: trunk });

  // Sleeves to the elbow: cloth, so they simply span the upper arm.
  for (const side of ['R', 'L'] as const) {
    kit.fittings.push({
      on: `upperArm${side}`,
      object: sleeve(brush, FEEL.ARM_RADIUS + 0.022, paints.cloth),
      fit: 'span',
    });
  }
};

/** Medium: a mail hauberk — sleeves to the elbow, skirt to the thigh. */
const buildHauberk: Builder = (brush, kit) => {
  const { paints } = brush;
  const trunk = new THREE.Group();

  // The body of the shirt, hanging from the shoulders. One tapered sleeve of
  // mail rather than a fitted shell: mail drapes, it does not hold a shape.
  const BODY_R2 = 0.178;
  const BODY_BOTTOM = -0.58;
  const bodyGeo = new THREE.CapsuleGeometry(
    BODY_R2,
    CHEST_TOP - BODY_BOTTOM - BODY_R2 * 2,
    8,
    22,
  );
  taperAlongY(bodyGeo, 1.12);
  const body = brush.add(bodyGeo, paints.main, trunk);
  body.scale.set(1, 1, 0.86);
  body.position.y = (CHEST_TOP + BODY_BOTTOM) / 2;

  // The skirt, split at the hem so it hangs to mid-thigh. This is most of the
  // extra COVERAGE a hauberk buys over a breastplate: it protects the legs zone
  // as well, which is why it looks like it is doing something.
  const skirtGeo = skirtGeometry(0.175, 0.235, 0.42, 22);
  const skirtMat = paints.main.clone();
  skirtMat.side = THREE.DoubleSide;
  brush.keep(skirtMat);
  const skirt = brush.add(skirtGeo, skirtMat, trunk);
  skirt.scale.set(1, 1, 0.9);
  skirt.position.y = WAIST_Y - 0.16;

  // A leather belt cinching the waist, which is what stops the skirt reading
  // as a dress.
  const belt = brush.add(new THREE.TorusGeometry(0.168, 0.024, 8, 24), paints.trim, trunk);
  belt.rotation.x = Math.PI / 2;
  belt.scale.set(1, 1, 0.9);
  belt.position.y = WAIST_Y - 0.01;
  const buckle = brush.add(new THREE.BoxGeometry(0.055, 0.05, 0.022), paints.trim, trunk);
  buckle.position.set(0, WAIST_Y - 0.01, -0.152);

  // Team colours, as a baldric across the mail.
  const baldric = brush.add(new THREE.BoxGeometry(0.075, 0.44, 0.02, 1, 4, 1), paints.team, trunk);
  baldric.position.set(0, -0.24, -0.166);
  baldric.rotation.z = 0.34;

  // Shoulder rolls where the mail bunches over the deltoid.
  for (const sign of [-1, 1] as const) {
    const roll = brush.add(new THREE.SphereGeometry(PAULDRON_R * 0.62, 18, 14), paints.main, trunk);
    roll.scale.set(1, 0.84, 1);
    roll.position.set(sign * SHOULDER_HALF_WIDTH, -0.012, 0);
  }

  kit.mounts.push({ at: 'chest', object: trunk });

  // Sleeves of mail to the elbow.
  for (const side of ['R', 'L'] as const) {
    kit.fittings.push({
      on: `upperArm${side}`,
      object: sleeve(brush, FEEL.ARM_RADIUS + 0.028, paints.main),
      fit: 'span',
    });
  }
};

/** Heavy: gothic breast and back plates, fauld, tassets — not a barrel. */
const buildCuirass: Builder = (brush, kit) => {
  const { paints } = brush;
  const trunk = new THREE.Group();

  // Breastplate: lathed peascod profile — broad chest, pinched waist, slight
  // point at the belly. Flattened on the front so it reads as beaten sheet.
  const breastGeo = latheProfile(
    [
      [CHEST_TOP + 0.02, 0.12],
      [CHEST_TOP - 0.04, 0.165],
      [CHEST_TOP - 0.16, 0.178],
      [WAIST_Y + 0.14, 0.17],
      [WAIST_Y + 0.04, 0.155],
      [WAIST_Y - 0.06, 0.148],
      [WAIST_Y - 0.18, 0.152],
    ],
    20,
  );
  // Keep enough Z so a side view still has a chest — hard flatten reads 2D.
  flattenFace(breastGeo, 'z', -1, 0.72);
  const breast = brush.add(breastGeo, paints.main, trunk);
  breast.scale.set(0.98, 1, 0.92);
  breast.position.z = -0.015;

  const backGeo = latheProfile(
    [
      [CHEST_TOP + 0.01, 0.11],
      [CHEST_TOP - 0.08, 0.16],
      [WAIST_Y + 0.08, 0.155],
      [WAIST_Y - 0.16, 0.145],
    ],
    18,
  );
  flattenFace(backGeo, 'z', 1, 0.72);
  const back = brush.add(backGeo, paints.main, trunk);
  back.scale.set(0.96, 1, 0.82);
  back.position.z = 0.03;

  // Central keel / tapul down the breast.
  const keel = brush.add(curvedPlate(0.04, 0.36, 0.035, 6), paints.trim, trunk);
  keel.position.set(0, -0.2, -0.175);

  const collar = brush.add(new THREE.TorusGeometry(0.14, 0.014, 10, 32), paints.trim, trunk);
  collar.rotation.x = Math.PI / 2;
  collar.scale.set(1, 1, 0.82);
  collar.position.y = CHEST_TOP - 0.01;

  const waist = brush.add(new THREE.TorusGeometry(0.155, 0.015, 10, 32), paints.trim, trunk);
  waist.rotation.x = Math.PI / 2;
  waist.scale.set(1, 1, 0.85);
  waist.position.y = WAIST_Y + 0.01;

  rivetRow(brush, trunk, paints.trim, 6, 0.145, CHEST_TOP - 0.05, Math.PI * 0.85);
  rivetRow(brush, trunk, paints.trim, 6, 0.15, WAIST_Y + 0.02, Math.PI * 0.85);

  // Gothic fluting either side of the keel.
  for (const sign of [-1, 1] as const) {
    for (let i = 0; i < 4; i += 1) {
      const flute = brush.add(new THREE.BoxGeometry(0.008, 0.22, 0.018, 1, 6, 1), paints.trim, trunk);
      flute.position.set(sign * (0.045 + i * 0.028), -0.18, -0.16 + i * 0.008);
      flute.rotation.set(0.08, 0, sign * (0.1 + i * 0.06));
      markNearDetail(flute);
    }
  }

  // Lance rest on the right breast.
  const rest = brush.add(new THREE.BoxGeometry(0.048, 0.022, 0.048), paints.trim, trunk);
  rest.position.set(0.125, -0.15, -0.1);
  rest.rotation.y = -0.4;
  const restArm = brush.add(new THREE.BoxGeometry(0.02, 0.018, 0.05), paints.main, trunk);
  restArm.position.set(0.14, -0.142, -0.12);
  restArm.rotation.set(-0.5, -0.4, 0);

  // Team tint as thin flush strips along the keel — never a proud cube.
  for (const sign of [-1, 1] as const) {
    const device = brush.add(new THREE.BoxGeometry(0.012, 0.2, 0.006), paints.team, trunk);
    device.position.set(sign * 0.028, -0.2, -0.165);
  }

  // Minimal shoulder wrap under the pauldron — must not compete with it.
  for (const sign of [-1, 1] as const) {
    const cap = brush.add(
      plateLame(PAULDRON_R * 0.32, PAULDRON_R * 0.28, 0.04, 12, Math.PI * 1.1),
      paints.main,
      trunk,
    );
    cap.rotation.z = sign * 0.35;
    cap.position.set(sign * SHOULDER_HALF_WIDTH, -0.015, 0);
  }

  kit.mounts.push({ at: 'chest', object: trunk });

  // Fauld: overlapping front-biased lames off the hips.
  const hips = new THREE.Group();
  for (let i = 0; i < 4; i += 1) {
    const lame = brush.add(
      plateLame(0.155 + i * 0.008, 0.165 + i * 0.01, 0.048, 24, Math.PI * 1.5),
      i === 1 ? paints.trim : paints.main,
      hips,
    );
    lame.scale.set(1, 1, 0.88);
    lame.position.y = 0.05 - i * 0.045;
  }
  // Tassets: curved plates over the thighs, not flat boxes.
  for (const sign of [-1, 1] as const) {
    const tasset = brush.add(curvedPlate(0.12, 0.15, 0.035, 5), paints.main, hips);
    tasset.position.set(sign * 0.085, -0.12, -0.085);
    tasset.rotation.x = -0.2;
    tasset.rotation.y = -sign * 0.15;
    const rivet = brush.add(new THREE.SphereGeometry(0.012, 10, 8), paints.trim, hips);
    rivet.position.set(sign * 0.085, -0.055, -0.1);
    markNearDetail(rivet);
  }
  kit.mounts.push({ at: 'pelvis', object: hips });

  // Rerebrace: slim closed taper. Couter is the second-biggest joint (refs).
  for (const side of ['R', 'L'] as const) {
    articulatedLimb(brush, kit, `upperArm${side}`, FEEL.ARM_RADIUS + 0.004, 5);

    const elbow = new THREE.Group();
    // Pointed couter cup — larger than the mid-arm tube, smaller than the pauldron.
    const couter = brush.add(new THREE.SphereGeometry(0.052, 18, 14), paints.main, elbow);
    couter.scale.set(1.1, 0.95, 1.25);
    // Outer wing / fan (period couter) — one side, not symmetric blobs.
    const wing = brush.add(curvedPlate(0.055, 0.06, 0.014, 4), paints.main, elbow);
    wing.position.set(side === 'R' ? 0.045 : -0.045, 0.005, 0.015);
    wing.rotation.z = side === 'R' ? 0.65 : -0.65;
    wing.rotation.x = -0.25;
    const tip = brush.add(new THREE.ConeGeometry(0.022, 0.045, 10), paints.main, elbow);
    tip.rotation.x = Math.PI / 2;
    tip.position.set(0, 0, 0.055);
    const rim = brush.add(new THREE.TorusGeometry(0.046, 0.004, 5, 16), paints.trim, elbow);
    rim.rotation.x = Math.PI / 2;
    kit.fittings.push({ on: `upperArm${side}`, object: elbow, fit: 'ride', t: 1 });
  }
};

// ---- legs ----

/** Light: wool breeches, cross-gartered from ankle to knee. */
const buildBreeches: Builder = (brush, kit) => {
  const { paints } = brush;
  for (const side of ['R', 'L'] as const) {
    kit.fittings.push({
      on: `thigh${side}`,
      object: sleeve(brush, THIGH_R + 0.016, paints.cloth),
      fit: 'span',
    });
    // The shin gets a wrapped legging plus gartering: three bands of cord
    // spiralling up it, which is the whole silhouette of the piece.
    //
    // The wrap is pushed as the fitting DIRECTLY rather than wrapped in a
    // group. `span` reads `userData.radius` to allow for the capsule's caps,
    // and a bare group has no radius — so the sleeve was scaled as if it were
    // cap-less and hung a cap's worth below the ankle, into the floor.
    kit.fittings.push({
      on: `shin${side}`,
      object: sleeve(brush, SHIN_R + 0.014, paints.cloth),
      fit: 'span',
    });
    for (let i = 0; i < 3; i += 1) {
      const band = new THREE.Group();
      const cord = brush.add(new THREE.TorusGeometry(SHIN_R + 0.02, 0.011, 8, 18), paints.trim, band);
      cord.rotation.set(Math.PI / 2, 0, 0.22);
      kit.fittings.push({ on: `shin${side}`, object: band, fit: 'ride', t: 0.22 + i * 0.26 });
    }
  }
};

/** Medium: mail chausses over the whole leg. */
const buildChausses: Builder = (brush, kit) => {
  const { paints } = brush;
  for (const side of ['R', 'L'] as const) {
    kit.fittings.push({
      on: `thigh${side}`,
      object: sleeve(brush, THIGH_R + 0.02, paints.main),
      fit: 'span',
    });
    kit.fittings.push({
      on: `shin${side}`,
      object: sleeve(brush, SHIN_R + 0.02, paints.main),
      fit: 'span',
    });
    // A leather strap at the knee, where mail hose is actually tied on.
    const knee = new THREE.Group();
    const strap = brush.add(new THREE.TorusGeometry(0.082, 0.014, 8, 18), paints.trim, knee);
    strap.rotation.x = Math.PI / 2;
    kit.fittings.push({ on: `shin${side}`, object: knee, fit: 'ride', t: 0 });
  }
};

/** Heavy: cuisses, winged poleyns and greaves — articulated plate, not tubes. */
const buildCuisses: Builder = (brush, kit) => {
  const { paints } = brush;
  for (const side of ['R', 'L'] as const) {
    articulatedLimb(brush, kit, `thigh${side}`, THIGH_R + 0.014, 3);
    articulatedLimb(brush, kit, `shin${side}`, SHIN_R + 0.014, 3);

    const knee = new THREE.Group();
    const cop = brush.add(curvedPlate(0.09, 0.075, 0.022, 4), paints.main, knee);
    cop.rotation.x = -0.35;
    const out = side === 'R' ? 1 : -1;
    const wing = brush.add(curvedPlate(0.055, 0.05, 0.012, 3), paints.main, knee);
    wing.rotation.y = Math.PI / 2;
    wing.position.x = out * 0.055;
    kit.fittings.push({ on: `shin${side}`, object: knee, fit: 'ride', t: 0 });

    const ankle = new THREE.Group();
    brush.add(plateLame(SHIN_R + 0.016, SHIN_R + 0.012, 0.035, 14, Math.PI * 0.9), paints.trim, ankle);
    kit.fittings.push({ on: `shin${side}`, object: ankle, fit: 'ride', t: 0.94 });
  }
};

// ---- boots ----

/**
 * A boot, mounted on the foot empty: origin at the ankle, toes towards -Z.
 *
 * The sole is placed FROM `ANKLE_HEIGHT` rather than chosen, so the boot rests
 * on the floor whatever the foot is doing. A shell placed by eye sinks a
 * centimetre into the flagstones, which `kit.test.ts` notices.
 */
function bootShell(brush: Brush, radius: number, mat: THREE.Material): THREE.Group {
  const boot = new THREE.Group();
  const shellGeo = new THREE.CapsuleGeometry(radius, 0.1, 6, 16);
  shellGeo.rotateX(Math.PI / 2);
  // Squashed vertically: a foot is much flatter than it is long.
  const squash = 0.76;
  shellGeo.scale(1.06, squash, 1);
  const shell = brush.add(shellGeo, mat, boot);
  // The capsule's half-height after the squash is what has to clear the sole.
  shell.position.set(0, -ANKLE_HEIGHT + radius * squash, -0.03);
  return boot;
}

/**
 * How high a boot's cuff reaches above the ankle, m.
 *
 * Sized against the foot damage zone rather than by eye: a boot that stopped
 * below `FOOT_ZONE_FRACTION` would be mitigating hits that land above the
 * leather you can see, which is the picture and the numbers disagreeing.
 */
const CUFF_TOP = FEEL.BODY_HEIGHT * FEEL.FOOT_ZONE_FRACTION - ANKLE_HEIGHT + 0.02;

/** Light: soft leather turnshoes with a cuff. */
const buildTurnshoes: Builder = (brush, kit) => {
  const { paints } = brush;
  for (const side of ['R', 'L'] as const) {
    const boot = bootShell(brush, 0.056, paints.main);
    const cuff = brush.add(new THREE.CylinderGeometry(0.06, 0.072, CUFF_TOP, 16), paints.main, boot);
    cuff.position.y = CUFF_TOP / 2 - 0.02;
    const lace = brush.add(new THREE.TorusGeometry(0.062, 0.008, 6, 16), paints.trim, boot);
    lace.rotation.x = Math.PI / 2;
    lace.position.y = -0.014;
    // A turned-down top, which is the whole look of a turnshoe.
    const fold = brush.add(new THREE.TorusGeometry(0.062, 0.014, 8, 18), paints.trim, boot);
    fold.rotation.x = Math.PI / 2;
    fold.position.y = CUFF_TOP - 0.024;
    kit.mounts.push({ at: `foot${side}`, object: boot });
  }
};

/** Medium: mail over the instep, leather beneath. */
const buildMailShoes: Builder = (brush, kit) => {
  const { paints } = brush;
  for (const side of ['R', 'L'] as const) {
    const boot = bootShell(brush, 0.058, paints.trim);
    // Mail only on the top of the foot; the sole stays leather, as it must.
    const instepGeo = new THREE.SphereGeometry(0.062, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2);
    const instep = brush.add(instepGeo, paints.main, boot);
    instep.scale.set(1.02, 0.8, 1.7);
    instep.position.set(0, -ANKLE_HEIGHT + 0.048, -0.042);
    const cuff = brush.add(new THREE.CylinderGeometry(0.064, 0.076, CUFF_TOP, 16), paints.main, boot);
    cuff.position.y = CUFF_TOP / 2 - 0.02;
    kit.mounts.push({ at: `foot${side}`, object: boot });
  }
};

/** Heavy: sabatons — flat overlapping foot plates to a blunt toe. */
const buildSabatons: Builder = (brush, kit) => {
  const { paints } = brush;
  for (const side of ['R', 'L'] as const) {
    const boot = bootShell(brush, 0.055, paints.cloth);
    for (let i = 0; i < 5; i += 1) {
      const t = i / 4;
      const lame = brush.add(curvedPlate(smooth(0.1, 0.045, t), 0.04, 0.02, 4), paints.main, boot);
      lame.rotation.x = Math.PI / 2 - 0.15;
      lame.position.set(0, -ANKLE_HEIGHT + 0.045 - t * 0.01, -0.02 - t * 0.09);
    }
    const cuff = brush.add(plateLame(0.065, 0.078, CUFF_TOP, 18, Math.PI * 1.7), paints.main, boot);
    cuff.position.y = CUFF_TOP / 2 - 0.02;
    const toe = brush.add(curvedPlate(0.045, 0.05, 0.025, 3), paints.trim, boot);
    toe.rotation.x = Math.PI / 2;
    toe.position.set(0, -ANKLE_HEIGHT + 0.035, -0.13);
    kit.mounts.push({ at: `foot${side}`, object: boot });
  }
};

// ---- gauntlets ----

/**
 * Builds a pair for the hands, and the forearm guards that go with them.
 *
 * The gauntlets slot owns the forearm, and that is deliberate: it is the slot
 * the sword-arm hurtbox maps to, so the piece that mitigates an arm hit should
 * be the piece you can see on the arm.
 *
 * ## Hand space
 *
 * The hand mount is oriented by `poseFigure`, and this is the frame it gives:
 *
 *   +Y  back up the forearm, towards the elbow — where a cuff goes
 *   -Y  out past the knuckles — where fingers go
 *
 * Everything below is built about that axis and is deliberately close to
 * rotationally SYMMETRIC around it. A gauntlet has to look right whether the
 * hand is palm-down in a guard or palm-up mid-cut, and the pose solver only
 * gives an arm direction, not a wrist roll — so a piece with a definite back
 * and palm would be right half the time and inside-out the rest.
 */
function handPair(
  brush: Brush,
  kit: BuiltKit,
  glove: (parent: THREE.Group) => void,
  bracerRadius: number,
  bracerMat: THREE.Material,
  /** When true, forearms use articulated plate instead of a capsule sleeve. */
  plateForearm = false,
): void {
  for (const side of ['R', 'L'] as const) {
    const hand = new THREE.Group();
    glove(hand);
    kit.mounts.push({ at: `hand${side}`, object: hand });
    if (plateForearm) {
      // Slim articulated vambrace — thinner than the couter above it.
      articulatedLimb(brush, kit, `forearm${side}`, bracerRadius, 5);
    } else {
      kit.fittings.push({
        on: `forearm${side}`,
        object: sleeve(brush, bracerRadius, bracerMat),
        fit: 'span',
      });
    }
  }
}

/** Light: leather strapping to the wrist. */
const buildHandwraps: Builder = (brush, kit) => {
  const { paints } = brush;
  handPair(
    brush,
    kit,
    (hand) => {
      const fist = brush.add(new THREE.SphereGeometry(0.062, 16, 12), paints.main, hand);
      fist.scale.set(0.94, 1.14, 0.94);
      // Strapping wound round the hand and up over the wrist.
      for (let i = 0; i < 4; i += 1) {
        const strap = brush.add(new THREE.TorusGeometry(0.058, 0.0085, 8, 18), paints.trim, hand);
        strap.rotation.set(Math.PI / 2, 0, 0.24);
        strap.position.y = -0.03 + i * 0.028;
      }
      const thumb = brush.add(new THREE.CapsuleGeometry(0.02, 0.03, 5, 10), paints.main, hand);
      thumb.position.y = -0.05;
    },
    FEEL.ARM_RADIUS + 0.014,
    paints.main,
  );
};

/** Medium: mail backs, bare palms so the grip still works. */
const buildMailMitts: Builder = (brush, kit) => {
  const { paints } = brush;
  handPair(
    brush,
    kit,
    (hand) => {
      const mitt = brush.add(new THREE.SphereGeometry(0.066, 18, 14), paints.main, hand);
      mitt.scale.set(0.96, 1.18, 0.96);
      // Leather where the hand closes on the grip: a band rather than a side,
      // so it reads the same at any wrist roll.
      const palm = brush.add(new THREE.TorusGeometry(0.056, 0.016, 8, 20), paints.trim, hand);
      palm.rotation.x = Math.PI / 2;
      palm.position.y = -0.026;
      const cuff = brush.add(new THREE.CylinderGeometry(0.058, 0.05, 0.06, 18), paints.main, hand);
      cuff.position.y = 0.058;
      const wrist = brush.add(new THREE.TorusGeometry(0.056, 0.011, 8, 18), paints.trim, hand);
      wrist.rotation.x = Math.PI / 2;
      wrist.position.y = 0.088;
    },
    FEEL.ARM_RADIUS + 0.02,
    paints.main,
  );
};

/**
 * Heavy: gauntlet sized to a real hand, meeting a slim tapered vambrace.
 * Hierarchy continues: shoulder > elbow > forearm > hand.
 */
const buildPlateGauntlets: Builder = (brush, kit) => {
  const { paints } = brush;
  handPair(
    brush,
    kit,
    (hand) => {
      const back = brush.add(curvedPlate(0.052, 0.062, 0.016, 4), paints.main, hand);
      back.rotation.x = 0.18;
      back.position.y = -0.006;

      for (let i = 0; i < 3; i += 1) {
        const lame = brush.add(curvedPlate(0.046 - i * 0.005, 0.016, 0.011, 3), paints.main, hand);
        lame.rotation.x = Math.PI / 2;
        lame.position.y = -0.042 - i * 0.017;
      }

      for (let f = 0; f < 4; f += 1) {
        const finger = brush.add(new THREE.CapsuleGeometry(0.007, 0.02, 4, 6), paints.main, hand);
        finger.position.set((f - 1.5) * 0.012, -0.1, -0.005);
        markNearDetail(finger);
      }

      // Wrist cuff matches the slim distal vambrace — no trumpet flare.
      const cuff = brush.add(
        new THREE.CylinderGeometry(0.038, 0.044, 0.05, 14, 1, false),
        paints.main,
        hand,
      );
      cuff.position.y = 0.045;
      const rim = brush.add(new THREE.TorusGeometry(0.043, 0.004, 5, 14), paints.trim, hand);
      rim.rotation.x = Math.PI / 2;
      rim.position.y = 0.07;
    },
    // Distal arm is the slimmest plate on the harness.
    FEEL.ARM_RADIUS + 0.002,
    paints.main,
    true,
  );
};

// ---- weapon ----

/**
 * Chrysamere, the Paladin's Blade — mounted on the blade's own rolled frame.
 *
 * A LEAF, not a bar: narrow at the ricasso, swelling to its full width a third
 * of the way up and running broad most of the way before it comes to a point.
 * That silhouette is the whole recognition, and it is also why this HIDES the
 * bare pill instead of decorating it — a round bar bulges out through the flats
 * of anything flatter than itself.
 *
 * THE CAPSULE IS UNCHANGED, which is the one hard rule here. The blade spans
 * exactly SWORD_LENGTH from the guard line, and its widest section is a hair
 * inside SWORD_RADIUS: half of 88mm across the flat and 26mm through the
 * thickness puts the corners 46mm off the axis against a 50mm capsule. Broad
 * enough to read as a claymore, and it still cannot promise a hit the server
 * would not give — measured in `kit.test.ts` rather than trusted.
 *
 * It ignores the wardrobe's palettes and uses its own gold, deliberately: every
 * other piece is kit and should look like one harness, but a named blade is
 * supposed to look like itself whoever is carrying it.
 */
const buildChrysamere: Builder = (brush, kit) => {
  const group = new THREE.Group();
  const { paints } = brush;
  // Chrysamere's own colours. `main` is the gold, `trim` the shadowed gold and
  // `cloth` the crimson, straight from the piece's palette in the catalogue.
  const gold = paints.main;
  const deepGold = paints.trim;
  const leather = paints.cloth;
  const edge = createSteelMaterial(0xe8dcae, { heavy: true });
  edge.metalness = 0.92;
  edge.roughness = 0.14;
  brush.keep(edge);
  const add = (geo: THREE.BufferGeometry, mat: THREE.Material): THREE.Mesh =>
    brush.add(geo, mat, group);

  const bladeLength = FEEL.SWORD_LENGTH;
  const gripLength = FEEL.SWORD_GRIP_LENGTH;

  // ---- the blade ----
  const WIDE = 0.088;
  const width = (t: number): number =>
    t < 0.32
      ? smooth(0.052, WIDE, t / 0.32)
      : t < 0.62
        ? WIDE
        : smooth(WIDE, 0.011, (t - 0.62) / 0.38);
  const thickness = (t: number): number => smooth(0.026, 0.008, t);

  // Dense leaf: many height segments so the profile reads as a curved fuller,
  // not a faceted wedge. Name `blade` is load-bearing for kit.test.ts.
  const bladeGeo = new THREE.BoxGeometry(1, bladeLength, 1, 2, 32, 2);
  profileAlong(bladeGeo, width, thickness);
  const blade = add(bladeGeo, gold);
  blade.name = 'blade';
  blade.position.y = bladeLength / 2;

  const edgeGeo = new THREE.BoxGeometry(1, bladeLength, 1, 2, 32, 2);
  profileAlong(
    edgeGeo,
    (t) => width(t) + 0.004,
    (t) => thickness(t) * 0.42,
  );
  const edging = add(edgeGeo, edge);
  edging.name = 'blade';
  edging.position.y = bladeLength / 2;

  const fullerGeo = new THREE.BoxGeometry(1, bladeLength * 0.6, 1, 2, 16, 2);
  profileAlong(
    fullerGeo,
    (t) => smooth(0.02, 0.009, t),
    (t) => smooth(0.031, 0.02, t),
  );
  const fuller = add(fullerGeo, deepGold);
  fuller.name = 'blade';
  fuller.position.y = 0.06 + bladeLength * 0.3;

  // ---- the hilt ----
  const collar = add(new THREE.BoxGeometry(0.056, 0.062, 0.044), gold);
  collar.position.y = 0.04;
  const throat = add(new THREE.BoxGeometry(0.07, 0.02, 0.052), deepGold);
  throat.position.y = 0.072;

  // Wings, not a bar: two quillons sweeping up towards the blade, built as a
  // short chain of blocks each turned a little further and a little smaller.
  const LINKS = 4;
  for (const side of [-1, 1] as const) {
    let x = side * 0.024;
    let y = 0.012;
    for (let i = 0; i < LINKS; i += 1) {
      const k = i / (LINKS - 1);
      const len = smooth(0.036, 0.024, k);
      const lift = smooth(0.16, 0.72, k);
      const link = add(new THREE.BoxGeometry(len, smooth(0.03, 0.019, k), smooth(0.05, 0.032, k)), gold);
      x += (side * len) / 2;
      y += (lift * len) / 2;
      link.position.set(x, y, 0);
      link.rotation.z = -side * lift;
      x += (side * len) / 2;
      y += (lift * len) / 2;
    }
    const finial = add(new THREE.SphereGeometry(0.016, 12, 10), gold);
    finial.scale.set(1.3, 1, 0.9);
    finial.position.set(x, y, 0);
    const boss = add(new THREE.SphereGeometry(0.01, 10, 8), leather);
    boss.position.set(side * 0.046, 0.026, -0.024);
  }
  for (const z of [-1, 1] as const) {
    const gem = add(new THREE.OctahedronGeometry(0.019, 0), leather);
    gem.scale.set(1, 1.35, 0.62);
    gem.position.set(0, 0.012, z * 0.03);
  }

  // ---- the grip ----
  const ferruleTop = add(new THREE.CylinderGeometry(0.03, 0.027, 0.026, 24), gold);
  ferruleTop.position.y = -0.014;
  const core = add(new THREE.CylinderGeometry(0.024, 0.026, gripLength * 0.82, 24), leather);
  core.position.y = -gripLength * 0.5;
  const ribs = 8;
  for (let i = 0; i < ribs; i += 1) {
    const rib = add(new THREE.TorusGeometry(0.026, 0.0055, 8, 24), deepGold);
    rib.rotation.x = Math.PI / 2;
    rib.position.y = -gripLength * ((i + 0.9) / (ribs + 0.6));
    markNearDetail(rib);
  }
  const ferruleBottom = add(new THREE.CylinderGeometry(0.028, 0.031, 0.024, 24), gold);
  ferruleBottom.position.y = -gripLength - 0.002;

  const pommel = add(new THREE.SphereGeometry(0.042, 28, 20), gold);
  pommel.scale.set(1.1, 0.88, 0.86);
  pommel.position.y = -gripLength - 0.036;
  for (const z of [-1, 1] as const) {
    const inlay = add(new THREE.SphereGeometry(0.014, 10, 8), leather);
    inlay.scale.set(1, 1, 0.6);
    inlay.position.set(0, -gripLength - 0.036, z * 0.032);
  }
  const peen = add(new THREE.CylinderGeometry(0.011, 0.014, 0.014, 8), deepGold);
  peen.position.y = -gripLength - 0.074;

  kit.mounts.push({ at: 'weapon', object: group });
  kit.hidesBareBlade = true;
};

/**
 * Reshapes a box into a blade by driving its width and thickness from profiles
 * along its own length. Segmented boxes only, and along Y only: the profiles
 * are sampled at whatever rings the geometry already has, so a box built with
 * one height segment comes out a wedge no matter what curve you hand it.
 */
function profileAlong(
  geometry: THREE.BufferGeometry,
  width: (t: number) => number,
  thick: (t: number) => number,
): void {
  const pos = geometry.attributes['position'] as THREE.BufferAttribute;
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const span = box.max.y - box.min.y || 1;
  for (let i = 0; i < pos.count; i += 1) {
    const t = (pos.getY(i) - box.min.y) / span;
    pos.setX(i, Math.sign(pos.getX(i)) * (width(t) / 2));
    pos.setZ(i, Math.sign(pos.getZ(i)) * (thick(t) / 2));
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
}

// ---------------------------------------------------------------------------
// assembly
// ---------------------------------------------------------------------------

/**
 * What each piece takes off the bare figure.
 *
 * Exhaustive by type alongside `BUILDERS`, so a new piece has to answer the
 * question rather than silently clipping. Empty is a legitimate answer: a hood
 * and a coif both leave the face open, so the head underneath has to stay.
 */
const HIDES: Record<PieceId, readonly BodyPart[]> = {
  hood: [],
  // Face open, so the head underneath stays.
  soldiersHelm: [],
  // Fully enclosed. The jaw and brow underneath were pushing through the visor.
  armet: ['head'],

  // A shoulder piece encloses the joint outright, whatever the chest is doing.
  spaulders: ['shoulders'],
  mailVoiders: ['shoulders'],
  pauldrons: ['shoulders'],

  cloak: ['torso', 'upperArms'],
  hauberk: ['torso', 'upperArms'],
  cuirass: ['torso', 'upperArms'],

  breeches: ['thighs', 'shins'],
  chausses: ['thighs', 'shins'],
  cuisses: ['thighs', 'shins'],

  turnshoes: ['feet'],
  mailShoes: ['feet'],
  sabatons: ['feet'],

  handwraps: ['hands', 'forearms'],
  mailMitts: ['hands', 'forearms'],
  plateGauntlets: ['hands', 'forearms'],

  // A sword replaces no part of a man.
  chrysamere: [],
};

/**
 * Which bones each joint sits between.
 *
 * Joints are DERIVED, never listed on a piece, and that is the whole point. A
 * shoulder or an elbow is not a body part anyone wears anything on — it is
 * flesh filling the gap where two limbs meet, and `span` ends a sleeve exactly
 * at the joint, so the far half of the sphere pokes through whatever is
 * covering the bone. Hand-listing them meant a hauberk remembered its shoulders
 * and forgot its elbows, and a cloak forgot both.
 *
 * The rule: a joint disappears only when EVERY bone meeting it is covered.
 * Cover one side and it is bare flesh showing at the hem of a sleeve, which is
 * exactly what it should look like.
 */
const JOINT_BETWEEN: Record<string, readonly BodyPart[]> = {
  shoulders: ['torso', 'upperArms'],
  elbows: ['upperArms', 'forearms'],
  knees: ['thighs', 'shins'],
};

/** Adds the joints whose every neighbouring bone is already hidden. */
function hideCoveredJoints(hides: Set<BodyPart>): void {
  for (const [joint, bones] of Object.entries(JOINT_BETWEEN)) {
    if (bones.every((bone) => hides.has(bone))) hides.add(joint as BodyPart);
  }
}

/**
 * One builder per piece id. Exhaustive by type, so adding a piece to the shared
 * catalogue without drawing it is a compile error rather than an invisible
 * player walking round in nothing.
 */
const BUILDERS: Record<PieceId, Builder> = {
  hood: buildHood,
  soldiersHelm: buildSoldiersHelm,
  armet: buildArmet,
  spaulders: buildSpaulders,
  mailVoiders: buildMailVoiders,
  pauldrons: buildPauldrons,
  cloak: buildCloak,
  hauberk: buildHauberk,
  cuirass: buildCuirass,
  breeches: buildBreeches,
  chausses: buildChausses,
  cuisses: buildCuisses,
  turnshoes: buildTurnshoes,
  mailShoes: buildMailShoes,
  sabatons: buildSabatons,
  handwraps: buildHandwraps,
  mailMitts: buildMailMitts,
  plateGauntlets: buildPlateGauntlets,
  chrysamere: buildChrysamere,
};

/** Builds one piece on its own, for the wardrobe preview and the tests. */
export function buildPiece(id: PieceId, isSelf: boolean): BuiltKit {
  const kit: BuiltKit = {
    mounts: [],
    fittings: [],
    disposables: [],
    hidesBareBlade: false,
    hides: new Set(HIDES[id]),
  };
  const paints = makePaints(PIECES[id], isSelf, kit.disposables);
  const brush: Brush = {
    paints,
    add(geo, mat, parent) {
      kit.disposables.push(geo);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      parent.add(mesh);
      return mesh;
    },
    keep(...items) {
      kit.disposables.push(...items);
    },
  };
  BUILDERS[id](brush, kit);
  return kit;
}

/**
 * Builds everything in a loadout as one kit.
 *
 * Rebuilt wholesale on every change rather than toggling visibility: loadouts
 * are picked rarely, and keeping sixteen pieces of geometry alive for every
 * player in the room to save one rebuild is the wrong trade.
 */
export function buildKit(loadout: Loadout, isSelf: boolean): BuiltKit {
  const kit: BuiltKit = {
    mounts: [],
    fittings: [],
    disposables: [],
    hidesBareBlade: false,
    hides: new Set(),
  };
  for (const id of Object.values(loadout)) {
    if (id === null) continue;
    const piece = buildPiece(id, isSelf);
    kit.mounts.push(...piece.mounts);
    kit.fittings.push(...piece.fittings);
    kit.disposables.push(...piece.disposables);
    kit.hidesBareBlade ||= piece.hidesBareBlade;
    for (const part of piece.hides) kit.hides.add(part);
  }
  // Derived last, over the WHOLE loadout: an elbow is covered by a mail sleeve
  // and a gauntlet together, and neither piece can know about the other.
  hideCoveredJoints(kit.hides);
  return kit;
}
